import { HandleStyles } from 'react-rnd';

export enum SortOrder {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

type DebounceFunction<T extends (...args: any[]) => void> = (
  func: T,
  delay: number
) => (...args: Parameters<T>) => void;

export const debounceFn: DebounceFunction<(...args: any[]) => void> = (
  func,
  delay
) => {
  let timer: ReturnType<typeof setTimeout>;

  return function (this: any, ...args: Parameters<typeof func>) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

/* Drag and Resize styles for Map Popup*/
export const dragAndResizeStyles: HandleStyles = {
  top: {
    top: '0',
    left: '52%',
    cursor: 'ns-resize',
    transform: 'translate(-50%, -50%)',
  },
  right: {
    right: '0',
    top: '50%',
    cursor: 'ew-resize',
    transform: 'translate(50%, -50%)',
  },
  bottom: {
    bottom: '0px',
    left: '0px',
    cursor: 'ns-resize',
    position: 'relative',
  },
  left: {
    left: '0',
    top: '52%',
    cursor: 'ew-resize',
    transform: 'translate(-50%, -50%)',
  },
  topRight: {
    top: '0',
    right: '0',
    cursor: 'nesw-resize',
    transform: 'translate(50%, -50%)',
  },
  bottomRight: {
    bottom: '-30px',
    right: '5px',
    cursor: 'nwse-resize',
    transform: 'translate(50%, 50%)',
  },
  bottomLeft: {
    bottom: '20px',
    left: '0',
    cursor: 'nesw-resize',
    transform: 'translate(-50%, 50%)',
    position: 'relative',
  },
  topLeft: {
    top: '10px',
    left: '8px',
    cursor: 'nwse-resize',
    transform: 'translate(-50%, -50%)',
  },
};
/* End Drag and Resize styles for Map Popup */

export const hexToRgba = (hex: string, opacity: number) => {
  const bigint = parseInt(hex.slice(1), 16); // Remove "#" and parse hex
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export function deepCloneArray<T>(array: T[]): T[] {
  return array.map((item) => JSON.parse(JSON.stringify(item)));
}

/**
 * Generic function to sort an array of objects by multiple properties.
 * @param array - The array of objects to sort.
 * @param properties - An array of property names to sort by, in order of priority.
 * @returns The sorted array.
 */
export function sortByProperties<T>(
  array: T[],
  criteria: [keyof T, SortOrder][]
): T[] {
  return array.sort((a, b) => {
    for (const [property, order] of criteria) {
      const valueA = a[property];
      const valueB = b[property];
      let comparison = 0;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        comparison = valueA.localeCompare(valueB);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        comparison = valueA - valueB;
      } else if (valueA instanceof Date && valueB instanceof Date) {
        comparison = valueA.getTime() - valueB.getTime();
      }

      // Adjust comparison for descending order
      if (order === SortOrder.DESCENDING) {
        comparison *= -1;
      }

      // If comparison is non-zero, return it to determine order
      if (comparison !== 0) return comparison;
    }

    return 0; // All properties are equal
  });
}
